import { lazy } from 'react';
import * as Yup from 'yup';
import { hasEnabledFeature, hasValue } from 'globalutils/fmUtils';

const DutyStatusRenderComponent = lazy(() => import('../DutyStatusRenderComponent'));
const DriverPerformanceRenderComponent = lazy(() => import('../DriverPerformanceRenderComponent'));
const EditedDataRenderComponent = lazy(() => import('../EditedDataRenderComponent'));
const DrivingEventRenderComponent = lazy(() => import('../DrivingEventRenderComponent'));
const VehicleReportRenderComponent = lazy(() => import('../VehicleReportRenderComponent'));
const MalFunctionReportRenderComponent = lazy(() => import('../MalFunctionReportRenderComponent'));
const DateReportFormRenderComponent = lazy(() => import('../DateReportFormRenderComponent'));
const DailyHOSDriverViolationsFormRenderComponent = lazy(() => import('../ExtraReports/DailyHOSDriverViolations'));
const CompanyFormRenderComponent = lazy(() => import('../ExtraReports/CompanyFormRenderComponent'));
const DailyUnsignedLogReportRenderComponent = lazy(() => import('../ExtraReports/DailyUnsignedLogReport'));
const DailyFailedInspectionReportFormRenderComponent = lazy(() => import('../ExtraReports/DailyFailedinspectionreport'));
const DailyUnidentifiedDrivingReportFormRenderComponent = lazy(() => import('../ExtraReports/DailyUnidentifieddrivingreport'));
const DutyStatusRenderComponentSpireon = lazy(() => import('../ExtraReports/DutyStatusRenderComponentSpireon'));

const getSchedulingFields = (fields) => {
  return hasEnabledFeature('downloadCenterEnabled')
    ? {
        recurrenceType: fields.recurrenceType,
        ...(fields.recurrenceType != 'NONE'
          ? {
              ...(fields.recurrenceType == 'RECURRING' ? { recurrenceInterval: fields.recurrenceInterval } : {}),
              scheduleEndDate: fields.scheduleEndDate ? new Date(fields.scheduleEndDate).getTime() : null,
            }
          : {}),
      }
    : {};
};

const getValidationSchema = ({ i18n }) => {
  return hasEnabledFeature('downloadCenterEnabled')
    ? {
        startDate: Yup.string().when('recurrenceType', {
          is: (value) => value === 'RECURRING',
          then: Yup.string(),
          otherwise: Yup.string().required(i18n.t('common.fieldRequiredMessage')),
        }),
        endDate: Yup.string().when('recurrenceType', {
          is: (value) => value === 'RECURRING',
          then: Yup.string(),
          otherwise: Yup.string().required(i18n.t('common.fieldRequiredMessage')),
        }),
      }
    : {
        startDate: Yup.string().required(i18n.t('common.fieldRequiredMessage')),
        endDate: Yup.string().required(i18n.t('common.fieldRequiredMessage')),
      };
};

export const reportFormConfigs = ({
  i18n,
  drivers,
  companies,
  regions,
  fetchTerminals,
  fetchDepartments,
  fetchSubDepartments,
  history,
  users,
  vehicles,
  Styled,
  apiStatus,
  callback,
}) => {
  return {
    dutyStatusReportForm: ({ fn }) => ({
      key: 'dutyStatusReportForm',
      apiId: 'dutyStatusReportForm',
      RenderComponent: DutyStatusRenderComponent,
      type: 'server',
      direction: 'column',
      initialValues: {
        startDate: '',
        endDate: '',
        regionId: '',
        terminalId: '',
        departmentId: '',
        subDepartmentId: '',
      },
      customProps: {
        hasDutyStatusApiStatus: hasValue(apiStatus.status, 'api', 'dutyStatusReportForm'),
        history,
        Styled,
        i18n,
        regions,
        fetchTerminals,
        fetchDepartments,
        fetchSubDepartments,
      },
      validationSchema: Yup.object().shape({
        startDate: Yup.string().required(i18n.t('common.fieldRequiredMessage')),
        endDate: Yup.string().required(i18n.t('common.fieldRequiredMessage')),
      }),
      handleOnSubmit: (fields) => {
        let data = {
          ...fields,
          departmentId: fields.departmentId?.value || '',
          regionId: fields.regionId?.value || '',
          subDepartmentId: fields.subDepartmentId?.value || '',
          terminalId: fields.terminalId?.value || '',
        };
        fn({
          data,
          apiId: 'dutyStatusReportForm',
          callback,
        });
      },
    }),

    dutyStatusReportFormSpireon: ({ fn }) => ({
      key: 'dutyStatusReportFormSpireon',
      apiId: 'dutyStatusReportForm',
      RenderComponent: DutyStatusRenderComponentSpireon,
      type: 'server',
      direction: 'column',
      initialValues: {
        startDate: '',
        endDate: '',
        regionId: '',
        terminalId: '',
        departmentId: '',
        subDepartmentId: '',
      },
      customProps: {
        hasDutyStatusApiStatus: hasValue(apiStatus.status, 'api', 'dutyStatusReportForm'),
        history,
        Styled,
        i18n,
        regions,
        fetchTerminals,
        fetchDepartments,
        fetchSubDepartments,
      },
      validationSchema: Yup.object().shape({
        startDate: Yup.string().required(i18n.t('common.fieldRequiredMessage')),
        endDate: Yup.string().required(i18n.t('common.fieldRequiredMessage')),
      }),
      handleOnSubmit: (fields) => {
        fn({ data: fields, apiId: 'dutyStatusReportForm', callback });
      },
    }),

    driverPerformanceReport: ({ fn }) => ({
      key: 'driverPerformanceReport',
      apiId: 'driverPerformanceReport',
      RenderComponent: DriverPerformanceRenderComponent,
      type: 'server',
      direction: 'column',
      initialValues: {
        startDate: '',
        endDate: '',
        driverIds: [],
        recurrenceType: 'NONE',
        recurrenceInterval: 'MONTHLY',
        scheduleEndDate: '',
      },
      customProps: {
        history,
        Styled,
        i18n,
        drivers,
      },
      validationSchema: Yup.object().shape({
        ...getValidationSchema({ i18n }),
      }),
      handleOnSubmit: (fields) => {
        let data = {
          endDate: fields.endDate,
          startDate: fields.startDate,
          driverIds: [],
          ...getSchedulingFields(fields),
        };
        fields.driverIds.forEach((item) => {
          data.driverIds.push(item.value);
        });
        fn({
          data: data,
          apiId: 'driverPerformanceReport',
          callback,
        });
      },
    }),

    editedDataReport: ({ fn }) => ({
      key: 'editedDataReport',
      apiId: 'editedDataReport',
      RenderComponent: EditedDataRenderComponent,
      type: 'server',
      direction: 'column',
      initialValues: {
        startDate: '',
        endDate: '',
        driverIds: [],
        recurrenceType: 'NONE',
        recurrenceInterval: 'MONTHLY',
        scheduleEndDate: '',
      },
      customProps: {
        history,
        Styled,
        i18n,
        users,
      },
      validationSchema: Yup.object().shape({
        ...getValidationSchema({ i18n }),
      }),
      handleOnSubmit: (fields) => {
        let data = {
          endDate: fields.endDate,
          startDate: fields.startDate,
          driverIds: [],
          ...getSchedulingFields(fields),
        };
        fields.driverIds.forEach((item) => {
          data.driverIds.push(item.value);
        });
        fn({ data: data, apiId: 'editedDataReport', callback });
      },
    }),

    drivingEventReport: ({ fn }) => ({
      key: 'drivingEventReport',
      apiId: 'drivingEventReport',
      RenderComponent: DrivingEventRenderComponent,
      type: 'server',
      direction: 'column',
      initialValues: {
        startDate: '',
        endDate: '',
        drivingState: { label: 'All', value: 'ALL' },
        driverIds: [],
        recurrenceType: 'NONE',
        recurrenceInterval: 'MONTHLY',
        scheduleEndDate: '',
      },
      customProps: {
        history,
        Styled,
        i18n,
        drivers,
      },
      validationSchema: Yup.object().shape({
        ...getValidationSchema({ i18n }),
      }),
      handleOnSubmit: (fields) => {
        let data = {
          endDate: fields.endDate,
          startDate: fields.startDate,
          drivingState: fields?.drivingState?.value || 'ALL',
          driverIds: [],
          ...getSchedulingFields(fields),
        };
        fields.driverIds.forEach((item) => {
          data.driverIds.push(item.value);
        });
        fn({ data: data, apiId: 'drivingEventReport', callback });
      },
    }),

    vehicleReport: ({ fn }) => ({
      key: 'vehicleReport',
      apiId: 'vehicleReport',
      RenderComponent: VehicleReportRenderComponent,
      type: 'server',
      direction: 'column',
      initialValues: {
        startDate: '',
        endDate: '',
        vehicleIds: [],
        recurrenceType: 'NONE',
        recurrenceInterval: 'MONTHLY',
        scheduleEndDate: '',
      },
      customProps: {
        history,
        Styled,
        i18n,
        vehicles,
      },
      validationSchema: Yup.object().shape({
        ...getValidationSchema({ i18n }),
      }),
      handleOnSubmit: (fields) => {
        let data = {
          endDate: fields.endDate,
          startDate: fields.startDate,
          vehicleIds: [],
          ...getSchedulingFields(fields),
        };
        fields.vehicleIds.forEach((item) => {
          data.vehicleIds.push(item.value);
        });
        fn({ data: data, apiId: 'vehicleReport', callback });
      },
    }),

    malFunctionReport: ({ fn }) => ({
      key: 'malFunctionReport',
      apiId: 'malFunctionReport',
      RenderComponent: MalFunctionReportRenderComponent,
      type: 'server',
      direction: 'column',
      initialValues: {
        startDate: '',
        endDate: '',
        vehicleIds: [],
        driverIds: [],
        recurrenceType: 'NONE',
        recurrenceInterval: 'MONTHLY',
        scheduleEndDate: '',
      },
      customProps: {
        history,
        Styled,
        i18n,
        vehicles,
        drivers,
      },
      validationSchema: Yup.object().shape({
        ...getValidationSchema({ i18n }),
      }),
      handleOnSubmit: (fields) => {
        let data = {
          endDate: fields.endDate,
          startDate: fields.startDate,
          vehicleIds: [],
          driverIds: [],
          ...getSchedulingFields(fields),
        };
        if (
          (fields && fields.driverIds && fields.driverIds.length > 0) ||
          (fields && fields.vehicleIds && fields.vehicleIds.length > 0)
        ) {
          if (fields.vehicleIds && fields.vehicleIds.length > 0) {
            fields.vehicleIds.forEach((item) => {
              data.vehicleIds.push(item.value);
            });
          }
          if (fields.driverIds && fields.driverIds.length > 0) {
            fields.driverIds.forEach((item) => {
              data.driverIds.push(item.value);
            });
          }
        }
        fn({ data: data, apiId: 'malFunctionReport', callback: callback });
      },
    }),

    DateReportForm: ({ fn }) => ({
      key: 'DateReportForm',
      apiId: 'DateReportForm',
      RenderComponent: DateReportFormRenderComponent,
      type: 'server',
      direction: 'column',
      initialValues: {
        startDate: '',
        endDate: '',
        recurrenceType: 'NONE',
        recurrenceInterval: 'MONTHLY',
        scheduleEndDate: '',
      },
      customProps: {
        history,
        Styled,
        i18n,
      },
      validationSchema: Yup.object().shape({
        ...getValidationSchema({ i18n }),
      }),
      handleOnSubmit: (fields) => {
        let data = {
          endDate: fields.endDate,
          startDate: fields.startDate,
          ...getSchedulingFields(fields),
        };
        fn({ data: data, apiId: 'DateReportForm', callback: callback });
      },
    }),

    companyFormReport: ({ fn }) => ({
      key: 'companyFormReport',
      apiId: 'companyFormReport',
      RenderComponent: CompanyFormRenderComponent,
      type: 'server',
      direction: 'column',
      initialValues: {
        startDate: '',
        endDate: '',
        companyIds: [],
        recurrenceType: 'NONE',
        recurrenceInterval: 'MONTHLY',
        scheduleEndDate: '',
      },
      customProps: {
        history,
        Styled,
        i18n,
        companies,
      },
      validationSchema: Yup.object().shape({
        ...getValidationSchema({ i18n }),
      }),
      handleOnSubmit: (fields) => {
        let data = {
          endDate: fields.endDate,
          startDate: fields.startDate,
          companyIds: [],
          ...getSchedulingFields(fields),
        };
        fields.companyIds.forEach((item) => {
          data.companyIds.push(item.value);
        });
        fn({ data: data, apiId: 'companyFormReport', callback: callback });
      },
    }),

    dailyHosViolationReportApi: ({ fn }) => ({
      key: 'dailyHosViolationReportApi',
      apiId: 'dailyHosViolationReportApi',
      type: 'server',
      direction: 'column',
      RenderComponent: DailyHOSDriverViolationsFormRenderComponent,
      initialValues: { startDate: '', endDate: '', driverIds: [] },
      customProps: {
        history,
        Styled,
        i18n,
        drivers,
      },
      validationSchema: Yup.object().shape({
        driverIds: Yup.array().min(1, i18n.t('common.fieldRequiredMessage')),
      }),
      handleOnSubmit: (fields) => {
        let data = {
          endDate: fields.endDate,
          startDate: fields.startDate,
          driverIds: [],
        };
        fields.driverIds.forEach((item) => {
          data.driverIds.push(item.value);
        });
        fn({
          data: data,
          apiId: 'dailyHosViolationReportApi',
          callback: callback,
        });
      },
      props: { drivers },
    }),

    dailyUnsignedLogReportApi: ({ fn }) => ({
      key: 'dailyUnsignedLogReportApi',
      apiId: 'dailyUnsignedLogReportApi',
      type: 'server',
      direction: 'column',
      RenderComponent: DailyUnsignedLogReportRenderComponent,
      initialValues: { startDate: '', endDate: '', driverIds: [] },
      customProps: {
        history,
        Styled,
        i18n,
        drivers,
      },
      validationSchema: Yup.object().shape({
        driverIds: Yup.array().min(1, i18n.t('common.fieldRequiredMessage')),
      }),
      handleOnSubmit: (fields) => {
        let data = {
          endDate: fields.endDate,
          startDate: fields.startDate,
          driverIds: [],
        };
        fields.driverIds.forEach((item) => {
          data.driverIds.push(item.value);
        });
        fn({
          data: data,
          apiId: 'dailyUnsignedLogReportApi',
          callback: callback,
        });
      },
      props: { drivers },
    }),

    dailyFailedInspectionReportApi: ({ fn }) => ({
      key: 'dailyFailedInspectionReportApi',
      apiId: 'dailyFailedInspectionReportApi',
      type: 'server',
      direction: 'column',
      RenderComponent: DailyFailedInspectionReportFormRenderComponent,
      initialValues: {
        startDate: '',
        endDate: '',
        driverIds: [],
      },
      customProps: {
        history,
        Styled,
        i18n,
        drivers,
      },
      validationSchema: Yup.object().shape({
        driverIds: Yup.array().min(1, i18n.t('common.fieldRequiredMessage')),
      }),
      handleOnSubmit: (fields) => {
        let data = {
          endDate: fields.endDate,
          startDate: fields.startDate,
          driverIds: [],
        };
        fields.driverIds.forEach((item) => {
          data.driverIds.push(item.value);
        });
        fn({
          data: data,
          apiId: 'dailyFailedInspectionReportApi',
          callback: callback,
        });
      },
    }),

    dailyUnidentifiedDrivingReportApi: ({ fn }) => ({
      key: 'dailyUnidentifiedDrivingReportApi',
      apiId: 'dailyUnidentifiedDrivingReportApi',
      type: 'server',
      direction: 'column',
      RenderComponent: DailyUnidentifiedDrivingReportFormRenderComponent,
      initialValues: {
        startDate: '',
        endDate: '',
        driverIds: [],
      },
      customProps: {
        history,
        Styled,
        i18n,
        drivers,
      },
      validationSchema: Yup.object().shape({}),
      handleOnSubmit: (fields) => {
        let data = {
          endDate: fields.endDate,
          startDate: fields.startDate,
          driverIds: [],
        };
        fields.driverIds.forEach((item) => {
          data.driverIds.push(item.value);
        });
        fn({
          data: data,
          apiId: 'dailyUnidentifiedDrivingReportApi',
          callback: callback,
        });
      },
    }),
  };
};
